import { useEffect, useState } from 'react'
import type { Dayjs } from 'dayjs'
import AdditionalFiltersDialog, {
  type AdditionalFilterField,
  type AdditionalFilterValue,
} from '@/components/common/AdditionalFiltersDialog'

export type MoreFilters = {
  meterNumber: string
  consumerName: string
  waterCard: string
  alertName: string
  openedFrom: Dayjs | null
  openedTo: Dayjs | null
  closedFrom: Dayjs | null
  closedTo: Dayjs | null
}

export const EMPTY_FILTERS: MoreFilters = {
  meterNumber: '',
  consumerName: '',
  waterCard: '',
  alertName: '',
  openedFrom: null,
  openedTo: null,
  closedFrom: null,
  closedTo: null,
}

type MoreFiltersVariant = 'notifications' | 'additional-actions'

// Only the filters /all accepts. Diameter, water identification, address,
// property type and group were hardcoded option lists the endpoint has no
// parameter for, so choosing one changed nothing.
const additionalActionsFields: AdditionalFilterField[] = [
  { name: 'meterNumber', label: 'מספר מד', type: 'text' },
  { name: 'consumerName', label: 'שם הצרכן', type: 'text' },
  { name: 'waterCard', label: 'כרטיס מים', type: 'text' },
  { name: 'alertName', label: 'שם ההתראה', type: 'text' },
]

const notificationFields: AdditionalFilterField[] = [
  { name: 'meterNumber', label: 'מספר מד', type: 'text' },
  { name: 'consumerName', label: 'שם הצרכן', type: 'text' },
  { name: 'waterCard', label: 'כרטיס מים', type: 'text' },
  { name: 'alertName', label: 'שם ההתראה', type: 'text' },
  {
    name: 'openedFrom',
    label: 'תאריך פתיחה מ-',
    type: 'date',
    notAfter: 'openedTo',
  },
  {
    name: 'openedTo',
    label: 'תאריך פתיחה עד',
    type: 'date',
    notBefore: 'openedFrom',
  },
  {
    name: 'closedFrom',
    label: 'תאריך סגירה מ-',
    type: 'date',
    notAfter: 'closedTo',
  },
  {
    name: 'closedTo',
    label: 'תאריך סגירה עד',
    type: 'date',
    notBefore: 'closedFrom',
  },
]

export default function MoreFiltersDialog({
  open,
  initial,
  onApply,
  onClose,
  variant = 'notifications',
}: {
  open: boolean
  initial: MoreFilters
  onApply: (filters: MoreFilters) => void
  onClose: () => void
  variant?: MoreFiltersVariant
}) {
  const [draft, setDraft] = useState<MoreFilters>(initial)

  useEffect(() => {
    setDraft(initial)
  }, [initial, open])

  const fields =
    variant === 'additional-actions'
      ? additionalActionsFields
      : notificationFields

  const handleChange = (name: string, value: AdditionalFilterValue) => {
    setDraft(previous => ({ ...previous, [name]: value }))
  }

  return (
    <AdditionalFiltersDialog
      open={open}
      fields={fields}
      values={draft}
      onChange={handleChange}
      onApply={() => {
        onApply(draft)
        onClose()
      }}
      onClear={() => {
        setDraft(EMPTY_FILTERS)
        onApply(EMPTY_FILTERS)
      }}
      onClose={onClose}
    />
  )
}
